import React, { useEffect, useState } from 'react';
import {
	Alert,
	Avatar,
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	Divider,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Paper,
	Grid,
} from '@mui/material';
import {
	Add,
	AutoAwesome,
	Cable,
	Check,
	CloudUpload,
	Link,
	PersonAdd,
	Upload,
	UploadFile,
	Visibility,
} from '@mui/icons-material';
import MainGrid from '../../components/MainGrid';
import { useNavigate } from 'react-router-dom';
import { getEnabledUserFeatures } from '../../context/MainProvider';
import { FEATURE_TYPES } from '../../Utils/Enums';
import {
	getAccessToken,
	getElderHomeId,
	getUserId,
} from '../../Utils/AuthUtil';
import {
	addElderDetails,
	fetchExternalEldersReview,
	sendElderDetailsForExtraction,
	storeUpdatedElder,
} from '../../services/ElderService';
import { toast } from 'react-toastify';
import { StarIcon, UserCheck } from '../../assets/icons/Icons';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Dropzone from 'react-dropzone';
import { helperText } from '../../assets/theme/Theme';
import ToastBanner from '../../components/ToastBanner';
import { border, dark, surface, teal } from '../../assets/theme/Colors';

const TAGS = [
	'Palliative Care — GSF Red stage',
	'Advanced Lung Cancer — Stage IV',
	'Bone Metastases',
	'Stroke',
	'Right Hemiplegia',
	'NG Tube',
];

const colors = [
	'#2563eb', // blue
	'#dc2626', // red
	'#00a89d', // green
	'#ca8a04', // yellow
	'#7c3aed', // purple
	'#ea580c', // orange
	'#0891b2', // cyan
	'#db2777', // pink
];

const errorState = { status: false, msg: '' };

const initialResFormData = {
	firstName: '',
	lastName: '',
	dob: '',
	roomNumber: '',
	careType: '',
	nhsNumber: '',
};

const MethodCard = ({ selected, icon, title, description, onClick }) => {
	return (
		<Card
			onClick={onClick}
			elevation={0}
			sx={{
				flex: 1,
				cursor: 'pointer',
				borderRadius: 3,
				border: `2px solid ${selected ? teal : border}`,
				background: selected ? '#e6f9f7' : '#fff',
				transition: '0.2s',
				'&:hover': {
					borderColor: teal,
					background: '#e6f9f7',
				},
			}}>
			<CardContent sx={{ textAlign: 'center', py: 4 }}>
				<Box sx={{ mb: '5px' }}>{icon}</Box>

				<Typography
					sx={{
						fontWeight: 700,
						color: dark,
						fontSize: 13,
						mb: 0.5,
					}}>
					{title}
				</Typography>

				<Typography sx={{ fontSize: 12, color: '#6b8888' }}>
					{description}
				</Typography>
			</CardContent>
		</Card>
	);
};

export default function OnboardResidentsScreen() {
	const token = getAccessToken();
	const home = getElderHomeId();
	const [method, setMethod] = useState('api');
	const navigate = useNavigate();
	const [name, setName] = useState('');
	const [txt, setTxt] = useState('');
	const [fileData, setFileData] = useState('');
	const [txtError, setTxtError] = useState({ status: false, msg: '' });
	const [isLoading, setIsLoading] = useState(false);
	const [residentFormData, setResidentFormData] =
		useState(initialResFormData);
	const [residentFormDataError, setResidentFormDataError] = useState({
		firstName: errorState,
		lastName: errorState,
		dob: errorState,
		roomNumber: errorState,
		careType: errorState,
		nhsNumber: errorState,
	});
	const [apiOnboardPendingResList, setApiOnboardPendingResList] = useState(
		[],
	);
	const [onShowApiOnboardResAlert, setOnShowApiOnboardResAlert] =
		useState(false);
	const [editingId, setEditingId] = useState(null);
	const [editForm, setEditForm] = useState({
		dateOfBirth: '',
		medicalHistory: '',
	});

	useEffect(() => {
		fetchResidents();
	}, []);

	async function fetchResidents() {
		const response = await fetchExternalEldersReview(home, token);

		if (response.message === 'Success') {
			setApiOnboardPendingResList(response.content.reverse());
		} else {
			toast.error(response.message);
		}
	}

	function validateInputs() {
		let isValid = true;

		const errors = {};

		if (!residentFormData.firstName) {
			errors.firstName = {
				status: true,
				msg: 'Enter first name',
			};
			isValid = false;
		}

		if (!residentFormData.lastName) {
			errors.lastName = {
				status: true,
				msg: 'Enter last name',
			};
			isValid = false;
		}

		if (!residentFormData.dob) {
			errors.dob = {
				status: true,
				msg: 'Enter date of birth',
			};
			isValid = false;
		}

		if (!residentFormData.careType) {
			errors.careType = {
				status: true,
				msg: 'Select care type',
			};
			isValid = false;
		}

		if (!residentFormData.roomNumber) {
			errors.roomNumber = {
				status: true,
				msg: 'Enter room number',
			};
			isValid = false;
		}

		if (!txt) {
			setTxtError({
				status: true,
				msg: 'Upload the text file',
			});

			isValid = false;
		}

		Object.keys(errors).length > 0 && setResidentFormDataError(errors);

		return isValid;
	}

	async function onAddResident(genPlanAfterStoring = false) {
		const isInputsValid = validateInputs();

		if (isInputsValid) {
			setIsLoading(true);

			const formData = new FormData();
			formData.append(
				'elder_details_file',
				new Blob([fileData], { type: 'text/plain' }),
				fileData.name,
			);
			formData.append(
				'resident_name',
				`${residentFormData.firstName} ${residentFormData.lastName}`,
			);
			formData.append('client_id', getElderHomeId());

			const data = await sendElderDetailsForExtraction(formData);

			if (!data.error) {
				const response = await storeElderData(data);
				if (genPlanAfterStoring && response.success) {
					navigate('/initialCarePlan', {
						state: { id: response.id },
					});
				}
			} else {
				toast.error(data.message);
				setIsLoading(false);
			}
		}
	}

	function getElderTags(count = 3) {
		const shuffled = [...TAGS].sort(() => Math.random() - 0.5);
		return shuffled.slice(0, count);
	}

	async function storeElderData(data) {
		const token = getAccessToken();
		const elderData = data.elder_info.Profile;
		const ignoringData = ['DateOfBirth', 'Age', 'Name', 'Gender'];

		let elderHistory = {};
		Object.keys(elderData).map(key => {
			if (!ignoringData.includes(key)) {
				const currentValue = Array.isArray(elderData[key])
					? elderData[key]
					: [elderData[key]];
				elderHistory = { ...elderHistory, [key]: currentValue };
			}
		});

		const formattedResidentFormData = toListMap(residentFormData);

		elderHistory = { ...elderHistory, ...formattedResidentFormData };

		const newElderData = {
			elderHomeId: getElderHomeId(),
			adminId: getUserId(),
			name: `${residentFormData.firstName} ${residentFormData.lastName}`,
			gender: elderData.Gender ? elderData.Gender : '',
			dateOfBirth: elderData.DateOfBirth ? elderData.DateOfBirth : 'N/A',
			age: elderData.Age ? parseInt(elderData.Age) : 0,
			elderHistory: elderHistory,
			tags: getElderTags(),
		};

		const formData = new FormData();
		formData.append(
			'elder',
			new Blob([JSON.stringify(newElderData)], {
				type: 'application/json',
			}),
		);
		formData.append('file', fileData);

		const response = await addElderDetails(formData, token);

		if (!response.error) {
			setName('');
			setTxt('');
			setFileData('');
			setResidentFormData(initialResFormData);
			toast.success('Resident added successfully', {
				style: {
					background: '#4F9E4E',
				},
			});
			setIsLoading(false);
			return { success: true, id: response.content.id };
		} else {
			toast.error(response.message);
			setIsLoading(false);
			return { success: false };
		}
	}

	const toListMap = obj =>
		Object.fromEntries(
			Object.entries(obj).map(([key, value]) => [
				key,
				value ? [String(value)] : [],
			]),
		);

	function readTextFile(e) {
		setTxt(e[0]);
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf', 'docx', 'xlsx'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		setTxtError({ status: false, msg: '' });
		setFileData(e[0]);
	}

	const handleChange = field => event => {
		setResidentFormData(prev => ({
			...prev,
			[field]: event.target.value,
		}));
	};

	async function onReviewedAllElder() {
		const penResidentIds = apiOnboardPendingResList.map(res => res.id);

		const body = {
			reviewed: true,
		};

		const results = await Promise.allSettled(
			penResidentIds.map(elderId =>
				storeUpdatedElder(token, body, `external/elder/${elderId}`),
			),
		);

		const allSuccess = results.every(
			result => result.status === 'fulfilled',
		);

		if (allSuccess) {
			toast.success('Elders reviewed successfully.');
			fetchResidents();
		} else {
			console.error('Some updates failed');
		}
	}

	const handleEdit = resident => {
		setEditingId(resident.id);
		setEditForm({
			dateOfBirth: resident?.dateOfBirth?.split('T')[0] || '',
			medicalHistory: resident?.medicalHistory || '',
		});
	};

	const handleApiChange = (field, value) => {
		setEditForm(prev => ({
			...prev,
			[field]: value,
		}));
	};

	const handleCancel = () => {
		setEditingId(null);
		setEditForm({
			dateOfBirth: '',
			medicalHistory: '',
		});
	};

	async function onSaveChange(id) {
		const route = `external/elder/${id}`;
		const response = await storeUpdatedElder(token, editForm, route);
		if (response.message === 'Success') {
			setEditingId(null);
			const clone = apiOnboardPendingResList.map(res =>
				res.id === id ? { ...res, ...editForm } : res,
			);
			setApiOnboardPendingResList(clone);
		} else {
			toast.error(response.message);
		}
	}

	return (
		<MainGrid>
			<Box sx={{ width: '100%' }}>
				{/* HEADER */}
				<Box sx={{ mb: 3 }}>
					<Typography
						sx={{
							fontSize: '22px',
							fontWeight: 600,
							color: dark,
							mb: 0.5,
						}}>
						Onboard Residents
					</Typography>

					<Typography sx={{ color: '#6b8888', fontSize: 14 }}>
						Import from your care system API or add residents
						manually with uploaded documents
					</Typography>
				</Box>

				{/* ALERT */}
				{onShowApiOnboardResAlert && (
					<Alert
						severity="info"
						sx={{
							mb: 3,
							borderRadius: 2,
							border: '1px solid #b5d4f4',
						}}>
						<Typography sx={{ fontSize: '13px' }}>
							<b>
								{apiOnboardPendingResList.length} residents
								pending from Care Vision API
							</b>{' '}
							- Import and confirm to add to SenzCare.
						</Typography>
					</Alert>
				)}

				{/* METHOD SELECT */}
				<Stack
					direction={{ xs: 'column', md: 'row' }}
					spacing={2}
					mb={3}>
					<MethodCard
						selected={method === 'api'}
						onClick={() => navigate('/settings')}
						icon={<Link sx={{ fontSize: 28, color: teal }} />}
						title="API Integration"
						description="Import from Care Vision, Arquella, or Oomph! via JSON config file"
					/>

					<MethodCard
						selected={method === 'manual'}
						onClick={() => setMethod('manual')}
						icon={<PersonAdd sx={{ fontSize: 28, color: teal }} />}
						title="Manual Onboarding"
						description="Enter details manually and upload resident documents for AI extraction"
					/>
				</Stack>

				{/* API VIEW */}
				{method === 'api' && (
					<Card
						sx={{
							border: '1px solid #d8e8e8',
							borderRadius: '12px',
						}}>
						<CardContent sx={{ p: 3 }}>
							{/* HEADER */}
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="space-between"
								flexWrap="wrap"
								gap={2}
								mb={2}>
								<Stack
									direction="row"
									spacing={1}
									alignItems="center">
									<Cable sx={{ color: teal }} />

									<Typography
										sx={{
											fontSize: 13,
											fontWeight: 500,
											color: dark,
										}}>
										{`API Import - ${apiOnboardPendingResList.length} Residents Pending Review`}
									</Typography>
								</Stack>

								<Chip
									icon={<AutoAwesome />}
									label="AI extracted & summarized"
									size="small"
									sx={{
										backgroundColor: dark,
										color: teal,
										fontWeight: 500,
										'& .MuiChip-icon': {
											color: teal,
										},
										padding: '5px',
										fontSize: 12,
									}}
								/>
							</Stack>

							{/* TABLE */}
							<TableContainer
								sx={{
									border: '1px solid #d8e8e8',
									borderRadius: '10px',
									mb: 2,
								}}>
								<Table>
									{/* HEADER */}
									<TableHead
										sx={{ backgroundColor: surface }}>
										<TableRow>
											{[
												'Resident',
												'DOB',
												'Gender',
												'Conditions Detected',
												'Status',
												'Action',
											].map(head => (
												<TableCell
													key={head}
													sx={{
														fontSize: 12,
														color: '#6b8888',
														fontWeight: 600,
														...(head ===
															'Conditions Detected' && {
															width: '40%',
														}),
													}}>
													{head}
												</TableCell>
											))}
										</TableRow>
									</TableHead>

									{/* BODY */}
									<TableBody>
										{apiOnboardPendingResList.length > 0 ? (
											apiOnboardPendingResList.map(
												(resident, index) => (
													<TableRow
														key={resident.id}
														hover={
															editingId !==
															resident.id
														}>
														{/* RESIDENT */}
														<TableCell>
															<Stack
																direction="row"
																spacing={1.25}
																alignItems="center">
																<Avatar
																	sx={{
																		width: 28,
																		height: 28,
																		fontSize: 11,
																		fontWeight: 600,
																		bgcolor:
																			colors[
																				index %
																					colors.length
																			],
																	}}>
																	{resident.name
																		.split(
																			' ',
																		)
																		.map(
																			w =>
																				w[0],
																		)
																		.join(
																			'',
																		)
																		.toUpperCase()
																		.slice(
																			0,
																			2,
																		)}
																</Avatar>

																<Typography
																	fontSize={
																		12
																	}>
																	{
																		resident.name
																	}
																</Typography>
															</Stack>
														</TableCell>

														{/* DOB */}
														<TableCell>
															{editingId ===
															resident.id ? (
																<TextField
																	type="date"
																	size="small"
																	value={
																		editForm.dateOfBirth
																	}
																	onChange={e =>
																		handleApiChange(
																			'dateOfBirth',
																			e
																				.target
																				.value,
																		)
																	}
																	sx={{
																		'& .MuiInputBase-root':
																			{
																				fontSize: 12,
																				height: 32,
																				borderRadius: 1.5,
																			},
																		'& input':
																			{
																				padding:
																					'6px 8px',
																			},
																	}}
																/>
															) : (
																<Typography
																	fontSize={
																		12
																	}
																	color="#4b5b5b">
																	{resident?.dateOfBirth?.split(
																		'T',
																	)[0] ||
																		'N/A'}
																</Typography>
															)}
														</TableCell>

														{/* GENDER */}
														<TableCell>
															<Typography
																fontSize={12}>
																{resident.gender ||
																	'N/A'}
															</Typography>
														</TableCell>

														{/* CONDITIONS / MEDICAL HISTORY */}
														<TableCell
															sx={{
																width: '40%',
															}}>
															{editingId ===
															resident.id ? (
																<TextField
																	value={
																		editForm.medicalHistory
																	}
																	onChange={e =>
																		handleApiChange(
																			'medicalHistory',
																			e
																				.target
																				.value,
																		)
																	}
																	multiline
																	minRows={2}
																	maxRows={4}
																	fullWidth
																	size="small"
																	sx={{
																		'& .MuiInputBase-root':
																			{
																				fontSize: 12,
																				borderRadius: 1.5,
																				backgroundColor:
																					'#fff',
																			},
																		'& .MuiInputBase-input':
																			{
																				padding:
																					'8px 10px',
																			},
																	}}
																/>
															) : (
																<Typography
																	fontSize={
																		12
																	}
																	sx={{
																		color: '#4b5b5b',
																		whiteSpace:
																			'pre-wrap',
																		lineHeight: 1.4,
																	}}>
																	{resident?.medicalHistory ||
																		'N/A'}
																</Typography>
															)}
														</TableCell>

														{/* STATUS */}
														<TableCell>
															<Chip
																label="Pending"
																size="small"
																sx={{
																	fontSize: 11,
																	backgroundColor:
																		'#fef3e2',
																	color: '#7c5200',
																	fontWeight: 500,
																	border: '1px solid #fac775',
																}}
															/>
														</TableCell>

														{/* ACTION */}
														<TableCell>
															{editingId ===
															resident.id ? (
																<Stack
																	direction="row"
																	spacing={1}>
																	<Button
																		size="small"
																		variant="contained"
																		sx={{
																			background:
																				teal,
																			color: dark,
																			fontWeight: 600,
																			borderRadius: 2,
																			height: 30,
																			fontSize: 12,
																			textTransform:
																				'none',
																			px: 2,
																			'&:hover':
																				{
																					background:
																						'#00b0a0',
																				},
																		}}
																		onClick={() =>
																			onSaveChange(
																				resident.id,
																			)
																		}>
																		Save
																	</Button>

																	<Button
																		size="small"
																		variant="text"
																		sx={{
																			fontSize: 11,
																			textTransform:
																				'none',
																			color: '#6b8888',
																		}}
																		onClick={
																			handleCancel
																		}>
																		Cancel
																	</Button>
																</Stack>
															) : (
																<Button
																	size="small"
																	variant="text"
																	sx={{
																		color: dark,
																		fontWeight: 600,
																		borderRadius: 2,
																		height: 30,
																		fontSize: 12,
																		textTransform:
																			'none',
																		px: 2,
																		'&:hover':
																			{
																				background:
																					'#00b0a0',
																			},
																	}}
																	onClick={() =>
																		handleEdit(
																			resident,
																		)
																	}>
																	Edit
																</Button>
															)}
														</TableCell>
													</TableRow>
												),
											)
										) : (
											<TableRow>
												<TableCell colSpan={6}>
													<Typography
														sx={{
															p: 3,
															display: 'flex',
															justifyContent:
																'center',
															color: '#6b8888',
															fontSize: 13,
															fontWeight: 500,
														}}>
														No pending residents.
													</Typography>
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>

							{/* AI SUMMARY */}
							{/* <Box
								sx={{
									backgroundColor: '#e6f9f7',
									border: '1px solid #b3ede8',
									borderRadius: '8px',
									p: 2,
									mb: 2,
								}}>
								<Box mb={1}>
									<Chip
										icon={<AutoAwesome />}
										label="AI summary"
										size="small"
										sx={{
											backgroundColor: dark,
											color: teal,
											fontWeight: 500,
											'& .MuiChip-icon': {
												color: teal,
											},
										}}
									/>
								</Box>

								<Typography
									sx={{
										fontSize: 12,
										lineHeight: 1.6,
										color: '#6b8888',
									}}>
									3 residents imported from Care Vision. All
									have active conditions.{' '}
									<Box
										component="span"
										sx={{
											fontWeight: 700,
											color: dark,
										}}>
										Dorothy Lee flagged for immediate care
										plan generation
									</Box>{' '}
									— no existing plan found.{' '}
									<Box
										component="span"
										sx={{
											fontWeight: 700,
											color: dark,
										}}>
										Alice Moore
									</Box>{' '}
									has Parkinson&apos;s and will require a
									mobility and falls risk assessment on
									admission.
								</Typography>
							</Box> */}

							{/* ACTIONS */}
							{apiOnboardPendingResList.length > 0 && (
								<Stack
									direction="row"
									spacing={1.5}
									flexWrap="wrap">
									<Button
										onClick={onReviewedAllElder}
										variant="contained"
										startIcon={<Check />}
										sx={{
											backgroundColor: teal,
											color: dark,
											fontWeight: 600,
											textTransform: 'none',
											borderRadius: '8px',
											boxShadow: 'none',
											'&:hover': {
												backgroundColor: '#00b0a0',
												boxShadow: 'none',
											},
										}}>
										Confirm & Save All
									</Button>

									{/* <Button
										variant="outlined"
										sx={{
											textTransform: 'none',
											borderRadius: '8px',
										}}>
										Review Individually
									</Button> */}
								</Stack>
							)}
						</CardContent>
					</Card>
				)}

				{/* MANUAL VIEW */}
				{method === 'manual' && (
					<Card
						sx={{
							borderRadius: '18px',
							border: '1px solid #d8e8e8',
							boxShadow: 'none',
						}}>
						<CardContent sx={{ p: 3 }}>
							{/* Title */}
							<Box sx={{ mb: 2 }}>
								<Typography
									sx={{
										fontSize: 14,
										fontWeight: 700,
										color: dark,
										display: 'flex',
										alignItems: 'center',
										gap: 1,
									}}>
									<Add sx={{ fontSize: 18, color: teal }} />
									Manual Resident Onboarding
								</Typography>
							</Box>

							{/* Row 1 */}
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: 'repeat(3, 1fr)',
									gap: 2,
									mb: 2,
								}}>
								<Box>
									<Typography
										sx={{
											fontSize: 12,
											fontWeight: 600,
											color: '#6b8888',
											mb: 0.7,
										}}>
										First Name
									</Typography>
									<TextField
										size="small"
										fullWidth
										value={residentFormData.firstName}
										placeholder="First name"
										onChange={handleChange('firstName')}
										sx={{
											'& .MuiInputBase-input': {
												fontSize: 13,
												'&::placeholder': {
													fontSize: 13,
												},
											},
										}}
										error={
											residentFormDataError.firstName
												?.status
										}
									/>
									{residentFormDataError.firstName
										?.status && (
										<Typography style={helperText}>
											{
												residentFormDataError.firstName
													?.msg
											}
										</Typography>
									)}
								</Box>

								<Box>
									<Typography
										sx={{
											fontSize: 12,
											fontWeight: 600,
											color: '#6b8888',
											mb: 0.7,
										}}>
										Last Name
									</Typography>
									<TextField
										size="small"
										fullWidth
										value={residentFormData.lastName}
										placeholder="Last name"
										onChange={handleChange('lastName')}
										sx={{
											'& .MuiInputBase-input': {
												fontSize: 13,
												'&::placeholder': {
													fontSize: 13,
												},
											},
										}}
										error={
											residentFormDataError.lastName
												?.status
										}
									/>
									{residentFormDataError.lastName.status && (
										<Typography style={helperText}>
											{residentFormDataError.lastName.msg}
										</Typography>
									)}
								</Box>

								<Box>
									<Typography
										sx={{
											fontSize: 12,
											fontWeight: 600,
											color: '#6b8888',
											mb: 0.7,
										}}>
										Date of Birth
									</Typography>
									<TextField
										size="small"
										fullWidth
										value={residentFormData.dob}
										placeholder="12/12/2000"
										onChange={handleChange('dob')}
										sx={{
											'& .MuiInputBase-input': {
												fontSize: 13,
												'&::placeholder': {
													fontSize: 13,
												},
											},
										}}
										error={
											residentFormDataError.dob?.status
										}
									/>
									{residentFormDataError.dob.status && (
										<Typography style={helperText}>
											{residentFormDataError.dob.msg}
										</Typography>
									)}
								</Box>
							</Box>

							{/* Row 2 */}
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: 'repeat(3, 1fr)',
									gap: 2,
									mb: 2,
								}}>
								<Box>
									<Typography
										sx={{
											fontSize: 12,
											fontWeight: 600,
											color: '#6b8888',
											mb: 0.7,
										}}>
										Room Number
									</Typography>
									<TextField
										size="small"
										fullWidth
										value={residentFormData.roomNumber}
										placeholder="Room Number"
										onChange={handleChange('roomNumber')}
										sx={{
											'& .MuiInputBase-input': {
												fontSize: 13,
												'&::placeholder': {
													fontSize: 13,
												},
											},
										}}
										error={
											residentFormDataError.roomNumber
												?.status
										}
									/>
									{residentFormDataError.roomNumber
										.status && (
										<Typography style={helperText}>
											{
												residentFormDataError.roomNumber
													.msg
											}
										</Typography>
									)}
								</Box>

								<Box>
									<Typography
										sx={{
											fontSize: 12,
											fontWeight: 600,
											color: '#6b8888',
											mb: 0.7,
										}}>
										Care Type
									</Typography>
									<TextField
										select
										size="small"
										fullWidth
										defaultValue="Select Care Type"
										onChange={handleChange('careType')}
										sx={{
											'& .MuiInputBase-input': {
												fontSize: 13,
												'&::placeholder': {
													fontSize: 13,
												},
											},
										}}
										error={
											residentFormDataError.careType
												?.status
										}>
										<MenuItem
											disabled
											value="Select Care Type">
											Select Care Type
										</MenuItem>
										<MenuItem value="Dementia">
											Dementia
										</MenuItem>
										<MenuItem value="Nursing">
											Nursing
										</MenuItem>
										<MenuItem value="Residential">
											Residential
										</MenuItem>
									</TextField>
									{residentFormDataError.careType.status && (
										<Typography style={helperText}>
											{residentFormDataError.careType.msg}
										</Typography>
									)}
								</Box>

								<Box>
									<Typography
										sx={{
											fontSize: 12,
											fontWeight: 600,
											color: '#6b8888',
											mb: 0.7,
										}}>
										NHS Number
									</Typography>
									<TextField
										size="small"
										fullWidth
										value={residentFormData.nhsNumber}
										placeholder="Optional"
										onChange={handleChange('nhsNumber')}
										sx={{
											'& .MuiInputBase-input': {
												fontSize: 13,
												'&::placeholder': {
													fontSize: 13,
												},
											},
										}}
									/>
								</Box>
							</Box>

							{/* Upload */}
							<Dropzone
								onDrop={acceptedFiles =>
									readTextFile(acceptedFiles)
								}>
								{({
									getRootProps,
									getInputProps,
									isDragActive,
								}) => (
									<Box sx={{ mb: 2 }}>
										<Typography
											sx={{
												fontSize: 11,
												fontWeight: 600,
												color: '#6b8888',
												mb: 1,
												textTransform: 'uppercase',
												letterSpacing: '0.8px',
											}}>
											Upload documents — AI extracts
											details automatically
										</Typography>

										<Box
											{...getRootProps()}
											sx={{
												border: `2px dashed ${txtError.status ? 'red' : '#b3ede8'}`,
												borderRadius: '12px',
												p: '28px 20px',
												textAlign: 'center',
												background: isDragActive
													? '#d0f5f1'
													: '#e6f9f7',
												cursor: 'pointer',
												transition: 'all 0.15s ease',
												'&:hover': {
													background: '#d0f5f1',
													borderColor: teal,
												},
											}}>
											<input {...getInputProps()} />

											<UploadFileIcon
												sx={{
													fontSize: 32,
													color: teal,
													mb: 1,
												}}
											/>
											{!txt ? (
												<>
													<Typography
														sx={{
															fontSize: 14,
															fontWeight: 600,
															color: dark,
															mb: 0.5,
														}}>
														Drag files here or click
														to upload
													</Typography>

													<Typography
														sx={{
															fontSize: 12,
															color: '#6b8888',
															lineHeight: 1.5,
														}}>
														GP letters, assessment
														forms, referral
														documents, medical
														history
													</Typography>
												</>
											) : (
												<Typography
													sx={{
														fontSize: 14,
														fontWeight: 600,
														color: dark,
														mb: 0.5,
													}}>
													{txt.name}
												</Typography>
											)}
										</Box>
									</Box>
								)}
							</Dropzone>
							{txtError.status && (
								<Typography style={helperText}>
									{txtError.msg}
								</Typography>
							)}

							{/* AI Extracted */}
							{/* <Box
								sx={{
									background: '#e6f9f7',
									border: '1px solid #b3ede8',
									borderRadius: '8px',
									p: 2,
									mb: 2,
								}}>
								<Box sx={{ mb: 1 }}>
									<Chip
										icon={<AutoAwesome />}
										label="AI extracted from uploaded GP letter"
										size="small"
										sx={{
											bgcolor: dark,
											color: teal,
											fontSize: 11,
										}}
									/>
								</Box>

								<Typography
									sx={{
										fontSize: 12,
										color: '#6b8888',
										lineHeight: 1.6,
									}}>
									Conditions:{' '}
									<b>Dementia Stage 2, Type 2 Diabetes</b> ·
									Medications:{' '}
									<b>Donepezil 5mg, Metformin 500mg</b> · GP:{' '}
									<b>Dr. Amara Singh — Westside Practice</b> ·
									Mobility: <b>Requires 1-person assist</b>
									<br />
									<span style={{ fontSize: 11 }}>
										Review and edit above before saving, or
										add more details manually.
									</span>
								</Typography>
							</Box> */}

							{/* Buttons */}
							<Box sx={{ display: 'flex', gap: 1 }}>
								<Button
									onClick={() => onAddResident(true)}
									disabled={isLoading}
									startIcon={<AutoAwesome />}
									sx={{
										background: teal,
										color: dark,
										textTransform: 'none',
										fontSize: 13,
										fontWeight: 700,
										borderRadius: '10px',
										px: 2,
										'&:hover': { background: '#00b0a0' },
									}}>
									Save & Generate Initial Care Plan
								</Button>

								<Button
									onClick={() => onAddResident(false)}
									variant="outlined"
									disabled={isLoading}
									startIcon={<UserCheck />}
									sx={{
										borderColor: border,
										color: '#1a2424',
										textTransform: 'none',
										fontSize: 13,
										borderRadius: '10px',
										'&:hover': {
											borderColor: teal,
											background: surface,
										},
									}}>
									Save Profile Only
								</Button>
							</Box>
						</CardContent>
					</Card>
				)}
			</Box>
			{isLoading && (
				<ToastBanner
					open={isLoading}
					message={'Onboarding Resident'}
					icon={<StarIcon color={'#FFFFFF'} />}
				/>
			)}
		</MainGrid>
	);
}
